import React, { useMemo } from 'react';
import { Alert, Col, Row } from 'react-bootstrap-5';

import UserLists from './user_lists';
import ToggleOverrideSection from '../shared/toggle_override_section';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo } from '@fortawesome/pro-solid-svg-icons';
import { isEqual } from 'lodash';
import TeamLists from './team_lists';

interface User {
  id: number;
  name: string;
}

interface UserPickerProps {
  allowedTeamIds?: number[] | null;
  allowedUserIds?: number[] | null;
  companyId?: number;
  defaultUserIds?: number[] | null;
  selectAll?: boolean;
  selectedTeamIds?: number[] | null;
  selectedUserIds?: number[] | null;
  users?: User[];
  teamsEnabled?: boolean;
  onChange: (payload: { userIds?: number[], teamIds?: number[], selectAll?: boolean }) => void;
}

export default function UserPicker({
  allowedTeamIds = null,
  allowedUserIds = null,
  companyId,
  defaultUserIds,
  selectAll,
  selectedTeamIds = [],
  selectedUserIds = [],
  teamsEnabled = false,
  onChange
}: UserPickerProps) {
  // A null list means the parent company didn't narrow the users down
  const restricted = allowedUserIds != null;
  const allowed = allowedUserIds || [];
  const handleAddUser = (id: number) => {
    const userIds = [...(selectedUserIds || []), id];
    onChange({ userIds });
  };

  const handleRemoveUser = (id: number) => {
    const userIds = [...(selectedUserIds || [])].filter(userId => userId !== id);
    onChange({ userIds });
  };

  const handleAddTeam = (id: number) => {
    const teamIds = [...(selectedTeamIds || []), id];
    onChange({ teamIds });
  };

  const handleRemoveTeam = (id: number) => {
    const teamIds = [...(selectedTeamIds || [])].filter(teamId => teamId !== id);
    onChange({ teamIds });
  };

  const handleSetDefaults = () => {
    const defaults = defaultUserIds || [];

    onChange({
      selectAll: false,
      userIds: restricted ? defaults.filter(id => allowed.includes(id)) : defaults
    });
  };

  const canSetDefault : boolean = useMemo(() => {
    return !!defaultUserIds && defaultUserIds.length > 0 &&
           !selectAll && !isEqual(defaultUserIds?.sort(), selectedUserIds?.sort());
  }, [defaultUserIds, selectAll, selectedUserIds]);

  const renderDefaultAlert = () => {
    if (!canSetDefault) return null;

    return (
      <Alert className="text-center" variant="info">
        <div>
          The users selected have been edited from the initial configuration.
          Selecting "revert" below will undo those changes,
          and make this document visible to those selected when this template was created.
        </div>
        <div className="btn btn-ghost btn-sm mt-1" onClick={handleSetDefaults}>
          <FontAwesomeIcon icon={faUndo} />
          Revert Available Users
        </div>
      </Alert>
    );
  };

  return (
    <div>
      <ToggleOverrideSection
        checked={selectAll}
        name="user-ids-override"
        subtitle={selectAll ?
          'Unchecking will allow you to grant access to this document template on a per-user basis' :
          'Checking will grant access to this document template for all users'
        }
        title={
          `Enable for all users${teamsEnabled ? ' and teams' : ''}${restricted ? ' in corporate defaults' : ''}`
        }
        onOverride={handleSetDefaults}
        onRemove={() => onChange({ selectAll: true })}
      />
      {renderDefaultAlert()}
      {!selectAll && (
        <UserLists
          allowedUserIds={allowedUserIds}
          companyId={companyId}
          selectedUserIds={selectedUserIds || []}
          onAddUser={handleAddUser}
          onRemoveUser={handleRemoveUser}
        />
      )}
      {teamsEnabled && !selectAll && (
        <TeamLists
          allowedTeamIds={allowedTeamIds}
          companyId={companyId}
          selectedTeamIds={selectedTeamIds || []}
          onAddTeam={handleAddTeam}
          onRemoveTeam={handleRemoveTeam}
        />
      )}
    </div>
  );
}
